/**
 * 客戶下拉的選項文字（10-07）：只印名字的話，同名客戶分不出來——ls-dev／ls-prod 都有兩個「Vossic」，
 * 一個直營、一個是某個多租戶平台的組織。`<option>` 只能放純文字，所以把類型與歸屬
 * 收成括號裡的幾段：`Vossic（公司・直營）`、`Vossic（公司・平台：<平台名稱>）`。
 *
 * 後台所有客戶下拉一律用這支，不要各自拼字串。
 */
import type { PlatformRef } from '@/api/ls'

/** 標示需要的欄位：我方後台的 `Customer` 與代理商 realm 的 `ResellerCustomer` 都吃得下。 */
export interface CustomerLabelSource {
  name: string
  domain?: string | null
  /** `company`／`personal`；null＝批81 之前的舊客戶，還沒補填。 */
  kind?: string | null
  reseller_id?: string | null
  reseller_name?: string | null
  /** 批141：來源平台（含已解除關聯的）；null＝直營／代理商客戶。 */
  platform?: PlatformRef | null
}

const KIND_SHORT: Record<string, string> = { company: '公司', personal: '個人' }

export function customerKindShort(kind: string | null | undefined): string {
  return kind ? (KIND_SHORT[kind] ?? kind) : '類型未填'
}

/**
 * 歸屬那一段：平台優先（平台客戶不能掛代理商）→ 代理商 → 直營。
 * 已解除關聯的平台客戶仍然是「平台來的」（一生只能屬於那個平台組織），只是多標一句。
 */
function ownershipPart(c: CustomerLabelSource): string {
  if (c.platform) return `平台：${c.platform.name}${c.platform.detached_at ? '（已解除）' : ''}`
  if (c.reseller_id) return `代理商：${c.reseller_name || '—'}`
  return '直營'
}

/**
 * `ownership: false`：不出歸屬段。代理商 realm 看到的客戶恆是自家客戶（store 把歸屬填成自己），
 * 標「代理商：自己」只是雜訊——代理商也會進的頁面傳 `{ ownership: !auth.isReseller }`。
 */
export function customerOptionLabel(c: CustomerLabelSource, opts: { ownership?: boolean } = {}): string {
  const parts = [customerKindShort(c.kind)]
  if (opts.ownership ?? true) parts.push(ownershipPart(c))
  if (c.domain) parts.push(`@${c.domain}`)
  return `${c.name}（${parts.join('・')}）`
}

/**
 * 能不能當擴充市集的候選（開通發行者、指派權利）：**平台客戶不行**，含已解除關聯的。
 * 平台客戶不開客戶 portal（沒有開發者區、不能輸入指派碼），也不在 CS 站台上裝擴充；
 * 判準與後端 `_projections.platform_by_customer` 相同（`platform_tenants` 有沒有這個客戶的列）。
 */
export function isMarketCandidate(c: Pick<CustomerLabelSource, 'platform'>): boolean {
  return !c.platform
}
